import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EASE = [0.76, 0, 0.24, 1];

export const Modal = ({ open, onClose, title, subtitle, testid, children }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        data-testid={testid}
        className="fixed inset-0 z-[9500] flex items-stretch md:items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="absolute inset-0 bg-[#1a130b]/70 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="relative z-10 w-full md:max-w-2xl bg-[#f2ece0] md:m-6 overflow-y-auto no-scrollbar max-h-[100svh] md:max-h-[90vh]"
        >
          <div className="p-7 md:p-12">
            <div className="flex items-start justify-between mb-8">
              <div>
                <p className="font-display text-[10px] tracking-[0.3em] uppercase text-[#a65a34] mb-3">{subtitle}</p>
                <h3 className="font-serif text-3xl md:text-5xl leading-[0.95] tracking-tight">{title}</h3>
              </div>
              <button data-testid="modal-close" onClick={onClose} className="mt-1 text-[#2a2018] hover:text-[#a65a34] transition-colors">
                <X size={26} />
              </button>
            </div>
            {children}
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

export const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-display text-[10px] tracking-[0.22em] uppercase text-black/50">{label}</span>
    {children}
  </label>
);

export const EditorialSelect = ({ value, onChange, placeholder, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger
      data-testid={testid}
      className="editorial-input rounded-none border-0 border-b border-black/25 px-0 h-14 focus:ring-0 focus:ring-offset-0 shadow-none text-base data-[placeholder]:text-black/35"
    >
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent className="bg-[#f2ece0] border-black/10">
      {options.map((o) => (
        <SelectItem key={o} value={o} className="font-body">{o}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const SubmitBtn = ({ loading, label, testid }) => (
  <button
    type="submit"
    data-testid={testid}
    disabled={loading}
    className="btn-invert group mt-4 inline-flex w-full items-center justify-center border border-[#2a2018] px-8 py-4 font-display text-[12px] uppercase tracking-[0.25em] text-[#2a2018] transition-colors duration-300 hover:text-[#f2ece0] disabled:opacity-50"
  >
    <span className="fill" />
    {loading ? "Sending…" : label}
  </button>
);

export const RegistrationForm = ({ onSuccess }) => {
  const CATS = ["Contemporary Womenswear", "Menswear", "Indo-Western", "Resort Wear", "Luxury Khadi", "Streetwear", "Accessories", "Sustainable Lifestyle Products"];
  const PROFILES = ["Independent Designer", "Fashion Student", "Rural Artisan", "Fashion Institute", "Entrepreneur"];
  const [f, setF] = useState({ full_name: "", email: "", phone: "", city: "", category: "", profile: "", portfolio: "", concept: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.category || !f.profile) { toast.error("Please choose a category and profile."); return; }
    setLoading(true);
    try {
      await api.post("/registrations", f);
      toast.success("Entry received. Welcome to the movement.");
      onSuccess?.();
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="registration-form">
      <div className="grid md:grid-cols-2 gap-6">
        <Field label="Full Name"><input required className="editorial-input" placeholder="Your name" value={f.full_name} onChange={set("full_name")} data-testid="reg-name" /></Field>
        <Field label="Email"><input required type="email" className="editorial-input" placeholder="you@email.com" value={f.email} onChange={set("email")} data-testid="reg-email" /></Field>
        <Field label="Phone"><input required className="editorial-input" placeholder="+91" value={f.phone} onChange={set("phone")} data-testid="reg-phone" /></Field>
        <Field label="City"><input required className="editorial-input" placeholder="City" value={f.city} onChange={set("city")} data-testid="reg-city" /></Field>
        <Field label="Category"><EditorialSelect testid="reg-category" value={f.category} onChange={set("category")} placeholder="Select a category" options={CATS} /></Field>
        <Field label="I am a"><EditorialSelect testid="reg-profile" value={f.profile} onChange={set("profile")} placeholder="Select profile" options={PROFILES} /></Field>
      </div>
      <Field label="Portfolio / Instagram (optional)"><input className="editorial-input" placeholder="https://" value={f.portfolio} onChange={set("portfolio")} data-testid="reg-portfolio" /></Field>
      <Field label="Your Collection Concept"><textarea required rows={3} className="editorial-input" placeholder="Describe your vision for contemporary Khadi…" value={f.concept} onChange={set("concept")} data-testid="reg-concept" /></Field>
      <SubmitBtn loading={loading} label="Submit Entry" testid="reg-submit" />
    </form>
  );
};

export const SponsorshipForm = ({ presetTier = "", onSuccess }) => {
  const TIERS = ["Title Partner", "Powered By Partner", "Associate Partner", "State Partner", "Education Partner", "Textile Partner", "Hospitality Partner", "Media Partner"];
  const [f, setF] = useState({ organization: "", contact_name: "", email: "", phone: "", tier: presetTier, message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.tier) { toast.error("Please select a partnership tier."); return; }
    setLoading(true);
    try {
      await api.post("/sponsorships", f);
      toast.success("Inquiry received. Our partnerships team will reach out.");
      onSuccess?.();
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="sponsorship-form">
      <div className="grid md:grid-cols-2 gap-6">
        <Field label="Organization"><input required className="editorial-input" placeholder="Company / Brand" value={f.organization} onChange={set("organization")} data-testid="sp-org" /></Field>
        <Field label="Contact Name"><input required className="editorial-input" placeholder="Your name" value={f.contact_name} onChange={set("contact_name")} data-testid="sp-name" /></Field>
        <Field label="Email"><input required type="email" className="editorial-input" placeholder="you@email.com" value={f.email} onChange={set("email")} data-testid="sp-email" /></Field>
        <Field label="Phone"><input required className="editorial-input" placeholder="+91" value={f.phone} onChange={set("phone")} data-testid="sp-phone" /></Field>
      </div>
      <Field label="Partnership Tier"><EditorialSelect testid="sp-tier" value={f.tier} onChange={set("tier")} placeholder="Select a tier" options={TIERS} /></Field>
      <Field label="Message"><textarea required rows={3} className="editorial-input" placeholder="Tell us about your partnership goals…" value={f.message} onChange={set("message")} data-testid="sp-message" /></Field>
      <SubmitBtn loading={loading} label="Send Inquiry" testid="sp-submit" />
    </form>
  );
};

export const ContactForm = () => {
  const [f, setF] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", f);
      toast.success("Message sent. Thank you for reaching out.");
      setF({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
      <div className="grid md:grid-cols-2 gap-6">
        <Field label="Name"><input required className="editorial-input" placeholder="Your name" value={f.name} onChange={set("name")} data-testid="ct-name" /></Field>
        <Field label="Email"><input required type="email" className="editorial-input" placeholder="you@email.com" value={f.email} onChange={set("email")} data-testid="ct-email" /></Field>
      </div>
      <Field label="Subject"><input required className="editorial-input" placeholder="How can we help?" value={f.subject} onChange={set("subject")} data-testid="ct-subject" /></Field>
      <Field label="Message"><textarea required rows={4} className="editorial-input" placeholder="Your message…" value={f.message} onChange={set("message")} data-testid="ct-message" /></Field>
      <SubmitBtn loading={loading} label="Send Message" testid="ct-submit" />
    </form>
  );
};
